import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { clearCache, getCache } from "../../util/Cache";
import { ArrowLeft, Edit2, Save, X, Mail, Phone, MapPin } from "lucide-react";
import type { Karyawan, Page } from "../../controller/mobile/types";

interface AccountData {
  email?: string;
  email_old?: string;
  username?: string;
  username_old?: string;
  password?: string;
}

type EditMode = "none" | "profile" | "account" | "password";

interface Props { 
  onNavigate: (page: Extract<Page, "home" | "capture" | "history" | "izin" | "izinlist" | "lembur" | "lemburlist" | "profile" | "none">) => void; 
}

export default function Profile({onNavigate}: Props): React.ReactElement {
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState<EditMode>("none");
  const [isLoading, setIsLoading] = useState(false);
  const [karyawan, setKaryawan] = useState<Karyawan>();
  const [formData, setFormData] = useState<AccountData>({
    email: karyawan?.email || "",
    email_old: karyawan?.email || "",
    username: karyawan?.username || "",
    username_old: "",
    password: ""
  });

  useEffect(() => {
    fetchUser();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const checkUserDashboard = async (): Promise<boolean> => {
      try {
        const data = await getCache("current_user");
        if (!data || !data.nama) {
          navigate("/");
          return false;
        }
        return true;
      } catch (error) {
        console.log(error);
        return false;
      }
  };

  const fetchUser = async () => {
    const response = await getCache('current_user');
    setKaryawan(response);
    setFormData({
      email: response?.email || "",
      email_old: response?.email || "",
      username: response?.username || "",
      username_old: response?.username || "",
      password: ""
    })
  }

  const handleSave = async () => {
    setIsLoading(true);
    const response = await fetchAkunEdit(formData);
    if(response.status === 200) { clearCache('current_user'); await checkUserDashboard(); }
    setEditMode("none");
    setIsLoading(false);
  };

  const handleCancel = () => {
    setEditMode("none");
  };

  const handlePassword = async (state : string) =>{
    if(state == 'to-password') setEditMode('password');
    else if (state == 'to-account') setEditMode('account');
    else if (state == 'confirm-password') { handleSave(); setEditMode("none"); }
  }

  const fetchAkunEdit = async (body : any) => {
    const nama_apps : string = process.env.REACT_APP_NAMA_APPS || "-";
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
      const formdata = new FormData();
      formdata.append("username", body.username);
      formdata.append("password", body.password);
      formdata.append("nama_apps", nama_apps);
      formdata.append("email", body.email);
      formdata.append("username_old", body.username_old);
      formdata.append("email_old", body.email_old);

      const requestOptions = {
        method: "PUT",
        body: formdata,
        redirect: "follow" as RequestRedirect
      };

      return await fetch( apiUrl + "/bawaslu_phwt/user", requestOptions)
        .then((response) => response.json())
        .then((result) => result)
        .catch((error) => { return { status : 500, message : "Terdatapat Error Server : " + error.getMessage() } });
    } catch (e : any) {
      return { status : 500, message : "Terdatapat Error Fetch : " + e.getMessage() }
    }
  }

  return (
    <div className="flex h-full flex-col bg-slate-50">
      {/* Header */}
      <header className="shrink-0 bg-slate-900 px-6 py-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => editMode !== "none" ? setEditMode("none") : onNavigate('home')}
              className="rounded-lg p-1 transition-colors hover:bg-slate-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-semibold">
              {editMode === "profile" ? "Edit Profil" : editMode === "account" ? "Edit Akun" : "Profil"}
            </h1>
          </div>
          {editMode === "none" && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setEditMode("profile")}
                className="rounded-lg bg-slate-700 px-3 py-2 text-sm font-medium transition-colors hover:bg-slate-600"
                hidden
              >
                Edit Profil
              </button>
              <button
                type="button"
                onClick={() => setEditMode("account")}
                className="rounded-lg bg-slate-700 px-3 py-2 text-sm font-medium transition-colors hover:bg-slate-600"
              >
                Edit Akun
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-5 py-5">
        {/* Profile Avatar Section */}
        <div className="mb-6 flex flex-col items-center rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-slate-200">
            <span className="text-3xl font-semibold text-slate-600">
              {karyawan?.nama?.charAt(0).toUpperCase() || "A"}
            </span>
          </div>
          {editMode === "none" && (
            <>
              <p className="mt-4 text-lg font-semibold text-slate-800">
                {karyawan?.nama || "Nama Pengguna"}
              </p>
              <p className="text-sm text-slate-500">
                {karyawan?.jabatan || "Belum ada jabatan"}
              </p>
            </>
          )}
        </div>

        {/* Account Information / Edit Form */}
        {editMode === "none" ? (
          // Display Mode
          <section className="space-y-3">
            {/* Email */}
            {karyawan?.email && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-center gap-3">
                  {/* <Mail className="h-4 w-4 text-slate-400" /> */}
                  <div className="flex-1">
                    <p className="text-xs font-medium uppercase text-slate-500">Email</p>
                    <p className="text-sm font-medium text-slate-800">{karyawan?.email}</p>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium uppercase text-slate-500">Username</p>
                    <p className="text-sm font-medium text-slate-800">{karyawan?.username}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Phone */}
            {karyawan?.no_hp && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-center gap-3">
                  {/* <Phone className="h-4 w-4 text-slate-400" /> */}
                  <div className="flex-1">
                    <p className="text-xs font-medium uppercase text-slate-500">No. HP</p>
                    <p className="text-sm font-medium text-slate-800">{karyawan?.no_hp}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Address */}
            {karyawan?.jabatan && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-start gap-3">
                  {/* <MapPin className="h-4 w-4 text-slate-400 mt-1 shrink-0" /> */}
                  <div className="flex-1">
                    <p className="text-xs font-medium uppercase text-slate-500">Status Karyawan</p>
                    <p className="text-sm font-medium text-slate-800">{karyawan?.jabatan}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Department */}
            {karyawan?.no_kary && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-medium uppercase text-slate-500">No. Karyawan</p>
                <p className="mt-2 text-sm font-medium text-slate-800">{karyawan?.no_kary}</p>
              </div>
            )}
          </section>
        ) : editMode === "profile" ? (
          // Edit Profile Mode
          <section className="space-y-4">
            {/* Nama */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <label className="text-xs font-medium uppercase text-slate-500">
                Nama Lengkap
              </label>
              {/* <input
                type="text"
                name="nama"
                value={formData.nama || ""}
                onChange={handleInputChange}
                disabled={isLoading}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-900 disabled:bg-slate-100"
              /> */}
            </div>

            {/* No HP */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <label className="text-xs font-medium uppercase text-slate-500">
                No. HP
              </label>
              {/* <input
                type="tel"
                name="no_hp"
                value={formData.no_hp || ""}
                onChange={handleInputChange}
                disabled={isLoading}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-900 disabled:bg-slate-100"
              /> */}
            </div>

            {/* Alamat */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <label className="text-xs font-medium uppercase text-slate-500">
                Alamat
              </label>
              {/* <textarea
                name="alamat"
                value={formData.alamat || ""}
                onChange={handleInputChange}
                disabled={isLoading}
                rows={3}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-900 disabled:bg-slate-100 resize-none"
              /> */}
            </div>

            {/* Jabatan */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <label className="text-xs font-medium uppercase text-slate-500">
                Jabatan
              </label>
              {/* <input
                type="text"
                name="jabatan"
                value={formData.jabatan || ""}
                onChange={handleInputChange}
                disabled={isLoading}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-900 disabled:bg-slate-100"
              /> */}
            </div>

            {/* Departemen */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <label className="text-xs font-medium uppercase text-slate-500">
                Departemen
              </label>
              {/* <input
                type="text"
                name="departemen"
                value={formData.departemen || ""}
                onChange={handleInputChange}
                disabled={isLoading}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-900 disabled:bg-slate-100"
              /> */}
            </div>

            {/* Action Buttons */}
            <div className="space-y-2 pt-4">
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-400"
              >
                <Save className="h-4 w-4" />
                {isLoading ? "Menyimpan..." : "Simpan Perubahan"}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
                className="flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50 disabled:bg-slate-100 disabled:text-slate-400"
              >
                <X className="h-4 w-4" />
                Batal
              </button>
            </div>
          </section>
        ) : editMode === "password" ? (
          <section className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <label className="text-xs font-medium uppercase text-slate-500">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password || ""}
                onChange={handleInputChange}
                disabled={isLoading}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-900 disabled:bg-slate-100"
              />
            </div>

            <button
                type="button"
                onClick={() => handlePassword('confirm-password')}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-400"
              >
                Konfirmasi Password
              </button>

           <button
              type="button"
              onClick={() => handlePassword('to-account')}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50 disabled:bg-slate-100 disabled:text-slate-400"
            >
              Kembali
            </button>
          </section>
        ) : (
          // Edit Account Mode
          <section className="space-y-4">
            {/* Email */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <label className="text-xs font-medium uppercase text-slate-500">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email || ""}
                onChange={handleInputChange}
                disabled={isLoading}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-900 disabled:bg-slate-100"
              />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <label className="text-xs font-medium uppercase text-slate-500">
                Username
              </label>
              <input
                type="username"
                name="username"
                value={formData.username || ""}
                onChange={handleInputChange}
                disabled={isLoading}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-900 disabled:bg-slate-100"
              />
            </div>

            {/* Password Info */}
            {/* <div className="rounded-2xl border border-slate-200 bg-white p-4"> */}
              {/* <label className="text-xs font-medium uppercase text-slate-500">
                Password
              </label> */}
              <button
                type="button"
                onClick={() => handlePassword('to-password')}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-400"
              >
                Ubah Password
              </button>
            {/* </div> */}

            {/* Action Buttons */}
            <div className="space-y-2 pt-4">
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-400"
              >
                <Save className="h-4 w-4" />
                {isLoading ? "Menyimpan..." : "Simpan Perubahan"}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
                className="flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50 disabled:bg-slate-100 disabled:text-slate-400"
              >
                <X className="h-4 w-4" />
                Batal
              </button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
